import { createFile, type MP4BoxBuffer, type Movie } from 'mp4box';
import { apiRequest, type UploadProgress, type Video } from './api';

export interface VideoInspection {
  durationSeconds: number;
  width: number;
  height: number;
  videoCodec: string;
  audioCodec?: string;
  mime: string;
}

export interface VideoPoster {
  blob: Blob;
  contentType: 'image/jpeg';
  sizeBytes: number;
  width: number;
  height: number;
  timestampSeconds: number;
}

export const MAX_POSTER_BYTES = 8 * 1024 * 1024;
export const MAX_POSTER_DIMENSION = 2200;
export const VIDEO_UPLOAD_CONCURRENCY = 3;
export const VIDEO_PART_RETRIES = 3;

export async function inspectVideoFile(file: File, signal?: AbortSignal): Promise<VideoInspection> {
  throwIfAborted(signal);
  if (!/\.mp4$/i.test(file.name) || file.type.toLowerCase() === 'video/quicktime') {
    throw new Error('Choose an .mp4 file. MOV files are not supported.');
  }
  const info = await inspectMp4Container(file, signal);
  throwIfAborted(signal);
  if (info.videoTracks.length !== 1) {
    throw new Error('MP4 video must contain exactly one video track.');
  }
  const videoCodec = info.videoTracks[0].codec;
  if (!isH264Codec(videoCodec)) {
    throw new Error('MP4 video must use H.264/AVC. HEVC and other codecs are not supported.');
  }
  const audioCodecs = info.audioTracks.map((track) => track.codec);
  if (audioCodecs.length > 1) {
    throw new Error('MP4 video can contain at most one audio track.');
  }
  if (audioCodecs.some((codec) => !isAcceptedAudioCodec(codec))) {
    throw new Error('MP4 audio must use AAC or MP3.');
  }
  const audioCodec = audioCodecs[0];
  const declaredMime = `video/mp4; codecs="${[videoCodec, ...audioCodecs].join(',')}"`;
  const playable = document.createElement('video').canPlayType(declaredMime);
  if (playable === '') {
    throw new Error('This browser cannot play the selected MP4 codecs.');
  }

  const playback = await loadVideoMetadata(file, signal);
  throwIfAborted(signal);
  const durationSeconds = playback.durationSeconds || info.duration / info.timescale;
  if (!Number.isFinite(durationSeconds) || durationSeconds <= 0 || playback.width <= 0 || playback.height <= 0) {
    throw new Error('The selected MP4 does not contain valid playback metadata.');
  }
  return {
    durationSeconds,
    width: playback.width,
    height: playback.height,
    videoCodec,
    audioCodec,
    mime: info.mime || declaredMime
  };
}

async function inspectMp4Container(file: File, signal?: AbortSignal): Promise<Movie> {
  return new Promise<Movie>((resolve, reject) => {
    const mp4 = createFile(false);
    let settled = false;
    mp4.onReady = (info) => {
      settled = true;
      resolve(info);
    };
    mp4.onError = (_module, message) => {
      if (!settled) {
        settled = true;
        reject(new Error(`Could not inspect MP4: ${message}`));
      }
    };

    void (async () => {
      try {
        const chunkBytes = 2 * 1024 * 1024;
        let offset = 0;
        while (!settled && offset < file.size) {
          throwIfAborted(signal);
          const end = Math.min(file.size, offset + chunkBytes);
          const buffer = (await file.slice(offset, end).arrayBuffer()) as MP4BoxBuffer;
          throwIfAborted(signal);
          buffer.fileStart = offset;
          const suggestedOffset = mp4.appendBuffer(buffer, end === file.size);
          const sequentialOffset = end;
          offset = suggestedOffset > sequentialOffset && suggestedOffset < file.size ? suggestedOffset : sequentialOffset;
        }
        if (!settled) {
          mp4.flush();
        }
        if (!settled) {
          settled = true;
          reject(new Error('The selected file is not a complete, readable MP4.'));
        }
      } catch (error) {
        if (!settled) {
          settled = true;
          reject(error);
        }
      }
    })();
  });
}

async function loadVideoMetadata(file: File, signal?: AbortSignal): Promise<{ durationSeconds: number; width: number; height: number }> {
  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.preload = 'auto';
  video.muted = true;
  video.playsInline = true;
  video.src = url;
  try {
    await waitForVideoEvent(video, 'loadeddata', signal);
    return { durationSeconds: video.duration, width: video.videoWidth, height: video.videoHeight };
  } finally {
    video.removeAttribute('src');
    video.load();
    URL.revokeObjectURL(url);
  }
}

export function waitForVideoEvent(video: HTMLVideoElement, eventName: 'loadeddata' | 'seeked', signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => finish(new Error('Timed out while reading the selected video.')), 15000);
    const finish = (error?: Error) => {
      window.clearTimeout(timeout);
      video.removeEventListener(eventName, handleSuccess);
      video.removeEventListener('error', handleError);
      signal?.removeEventListener('abort', handleAbort);
      error ? reject(error) : resolve();
    };
    const handleSuccess = () => finish();
    const handleError = () => finish(new Error('The selected MP4 could not be played by this browser.'));
    const handleAbort = () => finish(new DOMException('Video upload was cancelled.', 'AbortError'));
    video.addEventListener(eventName, handleSuccess, { once: true });
    video.addEventListener('error', handleError, { once: true });
    signal?.addEventListener('abort', handleAbort, { once: true });
    if (signal?.aborted) {
      handleAbort();
      return;
    }
    if (eventName === 'loadeddata') {
      video.load();
    }
  });
}

export async function captureVideoPoster(video: HTMLVideoElement, timestampSeconds: number, signal?: AbortSignal): Promise<VideoPoster> {
  throwIfAborted(signal);
  const timestamp = Math.max(0, Math.min(timestampSeconds, Number.isFinite(video.duration) ? video.duration : timestampSeconds));
  if (Math.abs(video.currentTime - timestamp) > 0.04) {
    const seeked = waitForVideoEvent(video, 'seeked', signal);
    video.currentTime = timestamp;
    await seeked;
  }
  const scale = Math.min(1, MAX_POSTER_DIMENSION / Math.max(video.videoWidth, video.videoHeight));
  const width = Math.max(1, Math.round(video.videoWidth * scale));
  const height = Math.max(1, Math.round(video.videoHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('This browser could not create a video poster.');
  }
  context.drawImage(video, 0, 0, width, height);
  throwIfAborted(signal);
  let quality = 0.9;
  let blob = await canvasToJpeg(canvas, quality);
  throwIfAborted(signal);
  while (blob.size > MAX_POSTER_BYTES && quality > 0.45) {
    quality -= 0.1;
    blob = await canvasToJpeg(canvas, quality);
    throwIfAborted(signal);
  }
  if (blob.size > MAX_POSTER_BYTES) {
    throw new Error('The selected poster frame could not be reduced below 8 MB.');
  }
  return { blob, contentType: 'image/jpeg', sizeBytes: blob.size, width, height, timestampSeconds: timestamp };
}

function canvasToJpeg(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Could not encode the video poster.'))), 'image/jpeg', quality);
  });
}

export async function uploadVideo(
  token: string,
  galleryId: string,
  file: File,
  inspection: VideoInspection,
  poster: VideoPoster,
  options: { onProgress?: (progress: UploadProgress) => void; signal?: AbortSignal } = {}
): Promise<{ video: Video }> {
  const report = (stage: UploadProgress['stage'], percent: number) =>
    options.onProgress?.({ fileName: file.name, stage, percent: Math.min(100, Math.max(0, Math.round(percent))) });
  report('requesting', 2);
  throwIfAborted(options.signal);

  let initiation: VideoUploadInitiation | undefined;
  try {
    initiation = await apiRequest<VideoUploadInitiation>(`/galleries/${galleryId}/videos/uploads`, {
      method: 'POST',
      token,
      signal: options.signal,
      body: {
        fileName: file.name,
        contentType: 'video/mp4',
        sizeBytes: file.size,
        durationSeconds: inspection.durationSeconds,
        width: inspection.width,
        height: inspection.height,
        videoCodec: inspection.videoCodec,
        audioCodec: inspection.audioCodec,
        posterTimestampSeconds: poster.timestampSeconds,
        previewContentType: poster.contentType,
        previewSizeBytes: poster.sizeBytes,
        previewWidth: poster.width,
        previewHeight: poster.height
      }
    });

    const parts = await uploadMultipartParts(file, initiation, options.signal, (percent) => report('uploading-video', 5 + percent * 0.85));
    throwIfAborted(options.signal);
    await uploadBlob(initiation.posterUploadUrl, poster.blob, poster.contentType, options.signal, (percent) => report('uploading-poster', 90 + percent * 0.06));
    report('completing-video', 97);
    const complete = () =>
      apiRequest<{ video: Video }>(`/galleries/${galleryId}/videos/uploads/${initiation!.videoId}/complete`, {
        method: 'POST',
        token,
        signal: options.signal,
        body: { uploadId: initiation!.uploadId, parts }
      });
    try {
      const result = await complete();
      report('complete', 100);
      return result;
    } catch {
      const result = await complete();
      report('complete', 100);
      return result;
    }
  } catch (error) {
    if (initiation) {
      await apiRequest<void>(`/galleries/${galleryId}/videos/uploads/${initiation.videoId}/abort`, {
        method: 'POST',
        token,
        body: { uploadId: initiation.uploadId }
      }).catch(() => undefined);
    }
    throw error;
  }
}

interface VideoUploadInitiation {
  videoId: string;
  storageKey: string;
  previewStorageKey: string;
  uploadId: string;
  partSize: number;
  partUrls: Array<{ partNumber: number; url: string }>;
  posterUploadUrl: string;
}

async function uploadMultipartParts(
  file: File,
  initiation: VideoUploadInitiation,
  signal: AbortSignal | undefined,
  onProgress: (percent: number) => void
): Promise<Array<{ partNumber: number; etag: string }>> {
  const results = new Array<{ partNumber: number; etag: string }>(initiation.partUrls.length);
  const loadedByPart = new Map<number, number>();
  let nextIndex = 0;
  const updateProgress = (partNumber: number, loaded: number) => {
    loadedByPart.set(partNumber, loaded);
    const totalLoaded = [...loadedByPart.values()].reduce((sum, value) => sum + value, 0);
    onProgress((totalLoaded / file.size) * 100);
  };

  const workers = Array.from({ length: Math.min(VIDEO_UPLOAD_CONCURRENCY, initiation.partUrls.length) }, async () => {
    while (nextIndex < initiation.partUrls.length) {
      const index = nextIndex++;
      const part = initiation.partUrls[index];
      const start = (part.partNumber - 1) * initiation.partSize;
      const body = file.slice(start, Math.min(file.size, start + initiation.partSize));
      results[index] = await uploadPartWithRetry(part, body, signal, (loaded) => updateProgress(part.partNumber, loaded));
    }
  });
  await Promise.all(workers);
  return results;
}

async function uploadPartWithRetry(
  part: { partNumber: number; url: string },
  body: Blob,
  signal: AbortSignal | undefined,
  onProgress: (loaded: number) => void
): Promise<{ partNumber: number; etag: string }> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= VIDEO_PART_RETRIES; attempt += 1) {
    throwIfAborted(signal);
    onProgress(0);
    try {
      const etag = await uploadPart(part.url, body, signal, onProgress);
      return { partNumber: part.partNumber, etag };
    } catch (error) {
      if (signal?.aborted) {
        throw new DOMException('Video upload was cancelled.', 'AbortError');
      }
      lastError = error;
    }
  }
  throw lastError instanceof Error ? lastError : new Error(`Video part ${part.partNumber} failed after retries.`);
}

function uploadPart(url: string, body: Blob, signal: AbortSignal | undefined, onProgress: (loaded: number) => void): Promise<string> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    const abort = () => request.abort();
    request.open('PUT', url);
    request.upload.onprogress = (event) => onProgress(event.loaded);
    request.onload = () => {
      signal?.removeEventListener('abort', abort);
      if (request.status >= 200 && request.status < 300) {
        const etag = request.getResponseHeader('etag');
        if (!etag) {
          reject(new Error('S3 did not return an ETag for a video part.'));
          return;
        }
        onProgress(body.size);
        resolve(etag);
        return;
      }
      reject(new Error(`Video part upload failed with status ${request.status}.`));
    };
    request.onerror = () => reject(new Error('A video part could not be uploaded because of a network error.'));
    request.onabort = () => reject(new DOMException('Video upload was cancelled.', 'AbortError'));
    signal?.addEventListener('abort', abort, { once: true });
    request.send(body);
  });
}

function uploadBlob(
  url: string,
  body: Blob,
  contentType: string,
  signal: AbortSignal | undefined,
  onProgress: (percent: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    const abort = () => request.abort();
    request.open('PUT', url);
    request.setRequestHeader('content-type', contentType);
    request.upload.onprogress = (event) => event.lengthComputable && onProgress((event.loaded / event.total) * 100);
    request.onload = () => {
      signal?.removeEventListener('abort', abort);
      if (request.status >= 200 && request.status < 300) {
        onProgress(100);
        resolve();
      } else {
        reject(new Error(`Poster upload failed with status ${request.status}.`));
      }
    };
    request.onerror = () => reject(new Error('The poster could not be uploaded because of a network error.'));
    request.onabort = () => reject(new DOMException('Video upload was cancelled.', 'AbortError'));
    signal?.addEventListener('abort', abort, { once: true });
    request.send(body);
  });
}

export function isH264Codec(codec: string): boolean {
  return /^(avc1|avc3)(\.[0-9a-f]+)?$/i.test(codec.trim());
}

export function isAcceptedAudioCodec(codec: string): boolean {
  return /^(mp4a\.40\.[0-9]+|mp4a\.(6b|69|6a)|mp3)$/i.test(codec.trim());
}

export function defaultPosterTimestamp(durationSeconds: number): number {
  return Math.min(1, Math.max(0, durationSeconds * 0.05));
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw new DOMException('Video upload was cancelled.', 'AbortError');
  }
}
